// frontend/src/hooks/useMandatoryCertCompliance.js
// React Query hooks for the Mandatory Certification compliance engine:
// rules CRUD + the per-rule compliance dashboard (Cert Compliance tab).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { configAPI } from '../services/api';
import toast from '../utils/toast';

export const useMandatoryCertRules = (params = {}) => {
  return useQuery({
    queryKey: ['certCompliance', 'rules', params],
    queryFn: async () => {
      const response = await configAPI.getMandatoryCertRules(params);
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — rules change rarely
  });
};

export const useMandatoryCertComplianceDashboard = () => {
  return useQuery({
    queryKey: ['certCompliance', 'dashboard'],
    queryFn: async () => {
      const response = await configAPI.getMandatoryCertComplianceDashboard();
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes — reflects live cert uploads
  });
};

export const useSafetyCertificationTypes = () => {
  return useQuery({
    queryKey: ['certCompliance', 'certTypes'],
    queryFn: async () => {
      const response = await configAPI.getSafetyCertificationTypes();
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes — catalog data
  });
};

export const useCreateMandatoryCertRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => configAPI.createMandatoryCertRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certCompliance'] });
      toast.success('Compliance rule created');
    },
    onError: (error) => {
      toast.error(error.response?.data?.name?.[0] || 'Failed to create rule');
    },
  });
};

export const useUpdateMandatoryCertRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => configAPI.updateMandatoryCertRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certCompliance'] });
      toast.success('Compliance rule updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.name?.[0] || 'Failed to update rule');
    },
  });
};

export const useDeleteMandatoryCertRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => configAPI.deleteMandatoryCertRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certCompliance'] });
      toast.success('Compliance rule deleted');
    },
    onError: () => {
      toast.error('Failed to delete rule');
    },
  });
};
